// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import styled, { css } from 'styled-components';

import { ellipsis, flexCenter, flexVerticalMiddle } from '../../../utils/style';

export const StyledHeader = styled.header<{ isLoggedIn?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 11;
  background-color: ${(props) => props.theme.colors.primary.main};
  box-shadow: 0 4px 4px 0 rgba(114, 127, 185, 0.1);
  padding: 0.25rem 1rem;
  display: grid;
  align-items: center;
  justify-content: center;
  column-gap: 1rem;

  ${(props) =>
    props.isLoggedIn
      ? css`
          height: var(--sp-login-header-height);
          grid-template:
            'hamburger logo .' auto
            'hamburger username .' auto / minmax(50px, auto)
            1fr minmax(50px, auto);
          row-gap: 0.25rem;
        `
      : css`
          height: var(--sp-header-height);
          grid-template:
            'testTag logo .' auto/ minmax(50px, auto)
            1fr minmax(50px, auto);
          row-gap: 0;
        `}

  ${({ theme }) => theme.mediaQueries.min.md} {
    height: var(--pc-header-height);
    grid-template: 'hamburger logo testTag username' auto / auto auto 1fr auto;
    row-gap: 0;
  }

  ${({ theme }) => theme.mediaQueries.min.lg} {
    padding: 0 1.5rem;
    grid-template: 'logo testTag username' auto / auto 1fr auto;
  }
`;

export const StyledHamburgerArea = styled.div`
  grid-area: hamburger;
  align-self: stretch;
  display: flex;
  padding-right: 1rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0.5rem;
    bottom: 0.5rem;
    right: 0;
    width: 1px;
    border-right: 1px solid ${(props) => props.theme.colors.greys.white};
  }

  ${({ theme }) => theme.mediaQueries.min.lg} {
    display: none;
  }
`;

export const StyledHamburgerButton = styled.div<{ open?: boolean }>`
  height: 34px;
  width: 34px;
  position: relative;
  margin: auto;
  padding-top: 4px;
  transition: 0.3s;
  cursor: pointer;

  & {
    .bar {
      height: 2px;
      width: 20px;
      display: block;
      margin: 5px auto;
      position: relative;
      background-color: ${(props) => props.theme.colors.greys.white};
      border-radius: ${(props) => props.theme.radii.default};
      transition: 0.4s;
    }
  }

  ${(props) =>
    props.open &&
    css`
      & {
        .bar {
          &:nth-of-type(1) {
            transform: translateY(7px) rotate(45deg);
          }

          &:nth-of-type(2) {
            opacity: 0;
          }

          &:nth-of-type(3) {
            transform: translateY(-7px) rotate(-45deg);
          }
        }
      }
    `}
`;

export const StyledLogo = styled.a`
  grid-area: logo;
  font-size: ${(props) => props.theme.fontSizes.small.fontSize};
  font-weight: bold;
  color: ${(props) => props.theme.colors.greys.white};
  text-decoration: none;
  ${flexVerticalMiddle}
  justify-content: center;

  & {
    .logo-title {
      margin-left: 0.5rem;
    }
  }

  ${({ theme }) => theme.mediaQueries.min.md} {
    margin-right: 1.5rem;
    justify-content: flex-start;
  }
`;

export const StyledTestEnvTag = styled.div`
  grid-area: testTag;
  background-color: ${(props) => props.theme.colors.greys.white};
  border: 1px solid ${(props) => props.theme.colors.error.primary};
  color: ${(props) => props.theme.colors.error.primary};
  font-size: ${(props) => props.theme.fontSizes.small.fontSize};
  ${flexCenter}
  padding: 0.25rem 0.5rem;
  display: none;

  ${({ theme }) => theme.mediaQueries.min.md} {
    display: flex;
    width: 6rem;
    height: 2.0625rem;
    font-size: ${(props) => props.theme.fontSizes.text.fontSize};
  }
`;

export const StyledUsername = styled.p`
  grid-area: username;
  font-size: ${(props) => props.theme.fontSizes.small.fontSize};
  line-height: 1.5;
  color: ${(props) => props.theme.colors.greys.white};
  display: flex;
  justify-content: center;
  overflow: hidden;

  & {
    .text {
      ${ellipsis}
      max-width: 50%;
    }
  }

  ${({ theme }) => theme.mediaQueries.min.md} {
    flex-direction: column;
    justify-content: flex-start;
    justify-items: stretch;

    & {
      .text {
        max-width: none;
      }
    }
  }
`;
